import React from "react";
import { Box } from "@mui/material";
import { useWindowSize } from "@uidotdev/usehooks";
import SabiasQue from "../SabiasQue";

// Template123: Oxford Street (Page13 guia-londres). Todo desde backend.
// page.texts[0]=header, texts[1]=intro, texts[2]=sectionTitle | sections[0..3]=lugares, sections[4]=transport
const Template123 = ({
	backgroundColor,
	textColor,
	headerBackgroundColor,
	headerTextColor,
	fontFamily,
	page,
}) => {
	const size = useWindowSize();
	const isSmallScreen = size.width <= 600;

	const headerText = page.texts?.[0]?.content;
	const logoImage = page.canvasImages?.find(
		(img) => img.id === "logo",
	)?.imageUrl;

	const introText = page.texts?.[1]?.content;
	const sectionTitle = page.texts?.[2]?.content;

	const getImageSrc = (id) => {
		if (page.images && Array.isArray(page.images)) {
			const found = page.images.find((img) => img?.id === id);
			if (found) return found.src ?? found.url;
		}
		return undefined;
	};

	const getCanvasImageUrl = (id) => {
		if (page.canvasImages && Array.isArray(page.canvasImages)) {
			const found = page.canvasImages.find((img) => img?.id === id);
			if (found && found.imageUrl) return found.imageUrl;
		}
		return undefined;
	};

	const heroImage = getImageSrc("oxford");
	const oxfordSticker = getCanvasImageUrl("oxford-sticker");

	const sections = Array.isArray(page?.sections)
		? page.sections
		: Array.isArray(page?.data?.sections)
			? page.data.sections
			: Array.isArray(page?.data?.data?.sections)
				? page.data.data.sections
				: [];
	const places = [sections[0], sections[1], sections[2], sections[3]].filter(
		Boolean,
	);
	const transportBlock = sections[4];

	const actualHeaderColor = headerBackgroundColor || "#de5624";
	const contentColor = textColor || "#444";

	const h3 = {
		fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
		fontSize: 14,
		fontWeight: 700,
		textTransform: "uppercase",
		margin: 0,
	};

	const h4 = {
		fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
		fontSize: 13,
		fontWeight: 600,
		margin: 0,
	};

	const p = {
		fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
		fontSize: 13,
		color: contentColor,
		margin: 0,
	};

	const imageStyle = {
		width: 70,
		height: 70,
		minWidth: 70,
		objectFit: "cover",
		borderRadius: 10,
	};

	const sabiasQuePositions = [
		{ top: isSmallScreen ? -18 : -65, left: "60%" },
		{ top: isSmallScreen ? -20 : -20, left: "58%" },
		null,
		{ top: -20, left: "60%" },
	];

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				minHeight: "100%",
				height: "auto",
				width: "100%",
				backgroundColor: backgroundColor || "#f7f7f7",
				color: contentColor,
				position: "relative",
				fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
				"& *:not([data-template-header])": {
					fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
					color: "inherit",
				},
			}}
		>
			{/* CABECERA */}
			{headerText && (
				<Box
					data-block-id="header"
					data-template-header
					sx={{
						backgroundColor: actualHeaderColor,
						color: headerTextColor || "#fff",
						textAlign: "center",
						padding: "15px 0",
						fontSize: "14px",
						fontWeight: 600,
						flexShrink: 0,
						position: "sticky",
						top: 0,
						zIndex: 99999,
					}}
				>
					Día 4: {headerText}
					{logoImage && !isSmallScreen && (
						<Box
							component="img"
							src={logoImage}
							alt="logo"
							sx={{
								width: isSmallScreen ? 66 : 80,
								position: "absolute",
								right: 2,
								top: 18,
								zIndex: 999999,
							}}
						/>
					)}
				</Box>
			)}

			{/* PORTADA */}
			<Box sx={{ position: "relative" }}>
				{heroImage && (
					<img
						src={heroImage}
						alt=""
						style={{ width: "100%", height: 200, objectFit: "cover" }}
					/>
				)}
				{oxfordSticker && (
					<img
						src={oxfordSticker}
						alt=""
						style={{ width: 90, position: "absolute", bottom: 60, left: 20 }}
					/>
				)}
				{headerText && (
					<Box
						component="h1"
						sx={{
							fontSize: "32px",
							position: "absolute",
							bottom: 0,
							margin: "10px",
							left: "20px",
							color: "#fff",
							textShadow: "2px 2px 4px rgba(0,0,0,0.6)",
						}}
					>
						{headerText}
					</Box>
				)}
			</Box>

			{introText && (
				<Box
					data-block-id="text-1"
					sx={{
						display: "flex",
						gap: "16px",
						padding: "10px 12px",
						position: "relative",
					}}
				>
					<Box component="p" sx={p}>
						{introText}
					</Box>
				</Box>
			)}

			{sectionTitle && (
				<Box
					component="h3"
					sx={{
						...h3,
						padding: "16px 12px 0",
						marginBottom: "8px",
						marginTop: "-20px",
					}}
				>
					{sectionTitle}
				</Box>
			)}

			<Box
				sx={{
					display: "flex",
					flexDirection: "column",
					gap: "10px",
					padding: "0 12px",
				}}
			>
				{places.map((section, idx) => {
					const imgSrc = section?.imageId
						? getImageSrc(section.imageId)
						: undefined;
					const pos = sabiasQuePositions[idx];
					return (
						<Box
							key={idx}
							data-block-id={`text-${idx + 2}`}
							sx={{
								display: "flex",
								gap: "16px",
								position: "relative",
								marginBottom: idx === 3 && isSmallScreen ? "12px" : 0,
							}}
						>
							{imgSrc && <img src={imgSrc} alt="" style={imageStyle} />}
							<Box>
								{section?.title && (
									<Box component="h4" sx={h4}>
										{section.title}
									</Box>
								)}
								{section?.body && (
									<Box component="p" sx={p}>
										{section.body}
									</Box>
								)}
							</Box>
							{section?.sabiasQue?.[0] && pos && (
								<Box
									sx={{
										marginTop: "6px",
										position: "absolute",
										top: pos.top,
										left: pos.left,
									}}
								>
									<SabiasQue
										icono={section.sabiasQue[0].icono}
										texto={section.sabiasQue[0].texto}
									/>
								</Box>
							)}
						</Box>
					);
				})}
			</Box>

			{/* Cómo llegar */}
			{(transportBlock?.title || transportBlock?.listItems?.length > 0) && (
				<Box data-block-id="text-6" sx={{ position: "relative", padding: "0px 12px" }}>
					<Box
						sx={{
							backgroundColor: "#f4e4df",
							border: "3px solid #de5624",
							borderRadius: "12px",
							padding: "8px 20px",
							fontSize: 12,
							color: "#333",
							marginTop: isSmallScreen ? "12px" : "12px",
							marginBottom: isSmallScreen ? "12px" : 0,
						}}
					>
						{transportBlock?.title && (
							<Box
								component="h4"
								sx={{
									margin: 0,
									marginBottom: "8px",
									fontSize: 13,
									fontWeight: 700,
									display: "flex",
									alignItems: "center",
									gap: 6,
									color: "#de5624!important",
									textTransform: "uppercase",
								}}
							>
								{transportBlock.title}
							</Box>
						)}
						{transportBlock?.listItems?.length > 0 && (
							<Box
								component="ul"
								sx={{ paddingLeft: "20px", margin: 0, lineHeight: 1.5 }}
							>
								{transportBlock.listItems.map((item, i) => (
									<Box component="li" key={i}>
										{item.title && <strong>{item.title}</strong>}
										{item.title && item.body ? " " : ""}
										{item.body}
									</Box>
								))}
							</Box>
						)}
					</Box>
				</Box>
			)}
		</Box>
	);
};

Template123.thumbnail = "/images/template-1.png";

export default Template123;
